import { useState, useRef, useEffect } from 'react';
import { BsBriefcase } from 'react-icons/bs';
import { BiSitemap } from 'react-icons/bi';
import { FaRegUser } from 'react-icons/fa';
import { BsCheckSquare, BsListTask, BsCalendar2Week, BsBug } from 'react-icons/bs';
import { IoChevronUpOutline } from 'react-icons/io5';
import { useModal } from '../context/ModalContext';
import { useProjectsStore } from '../state/projects';
import { useParams } from 'react-router';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface CreateDropdownProps {
  onSelectProject: () => void;
  onSelectRoom?: () => void;
  onSelectTeamMember?: () => void;
  onSelectTask?: () => void;
  onSelectActionItem?: () => void;
  onSelectDailyUpdate?: () => void;

}

export const CreateDropdown = ({ onSelectProject, onSelectTeamMember, onSelectTask, onSelectActionItem, onSelectDailyUpdate }: CreateDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const params = useParams<{ projectId: string }>();
  const projectData = useProjectsStore((state) => 
    state.fullProjects.find((p) => p.id === params.projectId)
  );
  const { setShowCreateRoomModal, setCreateRoomModalProps } = useModal();
  const { isFeatureEnabled } = useFeatureFlag();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleReportIssue = () => {
    window.open('mailto:support@finderz.ai?subject=Bug Report', '_blank');
    setIsOpen(false);
  };

  const handleCreateRoom = () => {
    // Get siteId from current project
    const siteId = typeof projectData === 'object' && projectData?.sites?.[0]?.site_id || projectData?.sites?.[0]?.id || '';
    
    setCreateRoomModalProps({
      projectId: params.projectId,
      siteId: siteId
    });
    setShowCreateRoomModal(true);
    setIsOpen(false);
  };



  return (
    <>
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="h-9 rounded-lg bg-[#171717] border border-[#171717] text-white px-4 py-1.5 cursor-pointer transition-all duration-150 hover:bg-[#333] flex items-center gap-2 text-[13px] font-medium"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
      >
        Create
        <IoChevronUpOutline className={`text-white/70 w-3 h-3 transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-48 bg-white rounded-xl border border-[#eaeaea] shadow-[0_4px_12px_rgba(0,0,0,0.08)] py-1" style={{ zIndex: 999999, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          {/* General Section */}
          <button
            onClick={() => {
              onSelectProject();
              setIsOpen(false);
            }}
            className="w-full px-3 py-1.5 text-left text-[13px] text-[#666] hover:text-[#171717] hover:bg-[#fafafa] flex items-center gap-2.5 transition-colors duration-100"
          >
            <BsBriefcase className="text-primary" />
            Project
          </button>
          {isFeatureEnabled('ROOMS_MANAGEMENT') && (
          <button
            onClick={handleCreateRoom}
            className="w-full px-3 py-1.5 text-left text-[13px] text-[#666] hover:text-[#171717] hover:bg-[#fafafa] flex items-center gap-2.5 transition-colors duration-100"
          >
            <BiSitemap className="text-primary" />
            Room
          </button>
          )}
          
          {/* Separator - only show if there are project content items */}
          {params.projectId && (onSelectTask || onSelectActionItem || onSelectDailyUpdate) && (
            <div className="mx-3 my-1 border-t border-[#eaeaea]"></div>
          )}
          
          {/* Project Content Section */}
          {onSelectTask && params.projectId && (
            <button 
              onClick={() => {
                onSelectTask();
                setIsOpen(false);
              }}
              className="w-full px-3 py-1.5 text-left text-[13px] text-[#666] hover:text-[#171717] hover:bg-[#fafafa] flex items-center gap-2.5 transition-colors duration-100"
            >
              <BsCheckSquare className="text-primary" />
              Task
            </button>
          )}
          {onSelectActionItem && params.projectId && (
            <button 
              onClick={() => {
                onSelectActionItem();
                setIsOpen(false);
              }}
              className="w-full px-3 py-1.5 text-left text-[13px] text-[#666] hover:text-[#171717] hover:bg-[#fafafa] flex items-center gap-2.5 transition-colors duration-100"
            >
              <BsListTask className="text-primary" />
              Action Item
            </button>
          )}
          {onSelectDailyUpdate && params.projectId && (
            <button 
              onClick={() => {
                onSelectDailyUpdate();
                setIsOpen(false);
              }}
              className="w-full px-3 py-1.5 text-left text-[13px] text-[#666] hover:text-[#171717] hover:bg-[#fafafa] flex items-center gap-2.5 transition-colors duration-100"
            >
              <BsCalendar2Week className="text-primary" />
              Daily Update
            </button>
          )}

          
          {/* Separator */}
          {onSelectTeamMember && params.projectId && (
            <div className="mx-3 my-1 border-t border-[#eaeaea]"></div>
          )}
          
          {/* Team Section */}
          {onSelectTeamMember && params.projectId && (
            <button 
              onClick={() => {
                onSelectTeamMember();
                setIsOpen(false);
              }}
              className="w-full px-3 py-1.5 text-left text-[13px] text-[#666] hover:text-[#171717] hover:bg-[#fafafa] flex items-center gap-2.5 transition-colors duration-100"
            >
              <FaRegUser className="text-primary" />
              Team Member
            </button>
          )}

          {/* Support Section — hidden on production */}
          {typeof window !== 'undefined' && !['app.finderz.ai', 'finderz.ai', 'www.finderz.ai'].includes(window.location.hostname) && (
            <>
              <div className="mx-3 my-1 border-t border-[#eaeaea]"></div>
              <button
                onClick={handleReportIssue}
                className="w-full px-3 py-1.5 text-left text-[13px] text-[#666] hover:text-[#171717] hover:bg-[#fafafa] flex items-center gap-2.5 transition-colors duration-100"
              >
                <BsBug className="text-orange-500" />
                Report Issue
              </button>
            </>
          )}
        </div>
      )}
    </div>


    </>
  );
}; 